import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { filterMovies, paginate, type SortKey } from "@/lib/movie";
import FilterPanel, { type CategoryFilters } from "./components/FilterPanel";

const DEFAULTS: CategoryFilters = {
  type: "全部",
  region: "全部",
  year: "全部",
  sort: "updated",
};

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const filters: CategoryFilters = {
    type: searchParams.get("type") || DEFAULTS.type,
    region: searchParams.get("region") || DEFAULTS.region,
    year: searchParams.get("year") || DEFAULTS.year,
    sort: searchParams.get("sort") || DEFAULTS.sort,
  };
  const page = Number(searchParams.get("page") || 1);

  const result = useMemo(
    () =>
      filterMovies({
        type: filters.type,
        region: filters.region,
        year: filters.year,
        sort: filters.sort as SortKey,
      }),
    [filters.type, filters.region, filters.year, filters.sort]
  );

  const paged = paginate(result, Number.isNaN(page) ? 1 : page);

  const title =
    filters.type !== "全部"
      ? `${filters.type}电影`
      : filters.region !== "全部"
      ? `${filters.region}电影`
      : "电影分类";

  useSeo({
    title: `${title} - 蜜桃成熟时在线观看`,
    description: `${title}合集，共 ${result.length} 部影片。可按类型、地区、年份筛选，支持按更新时间、热门度与评分排序，快速找到想看的高清电影。`,
    keywords: `${title},蜜桃成熟时在线观看,高清电影,在线观看电影,电影分类`,
  });

  const updateFilter = (key: keyof CategoryFilters, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === DEFAULTS[key as keyof CategoryFilters] && key !== "sort") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(nextPage));
    }
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeChips = [
    filters.type !== "全部" ? { key: "type" as const, value: filters.type } : null,
    filters.region !== "全部" ? { key: "region" as const, value: filters.region } : null,
    filters.year !== "全部" ? { key: "year" as const, value: filters.year } : null,
  ].filter(Boolean) as { key: keyof CategoryFilters; value: string }[];

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />
          <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
                Movie Library
              </p>
              <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                从爱情、动作到悬疑与治愈，按类型、地区、年份自由组合筛选，找到今晚想看的那一部。
              </p>
            </div>
            <div className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
              <div>
                <p className="font-label text-2xl font-extrabold text-primary-600">{result.length}</p>
                <p className="text-[11px] text-foreground-500">部符合条件的影片</p>
              </div>
              <span className="h-8 w-px bg-background-300" />
              <div>
                <p className="font-label text-2xl font-extrabold text-foreground-800">{paged.totalPages}</p>
                <p className="text-[11px] text-foreground-500">页结果</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="lg:hidden">
          <button
            type="button"
            onClick={() => setMobilePanelOpen((prev) => !prev)}
            className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800"
          >
            <span className="flex items-center gap-2">
              <i className="ri-equalizer-line text-base w-4 h-4 flex items-center justify-center text-primary-600" />
              筛选条件
              {activeChips.length > 0 && (
                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] text-primary-700">
                  {activeChips.length}
                </span>
              )}
            </span>
            <i className={`${mobilePanelOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg w-5 h-5 flex items-center justify-center`} />
          </button>
        </div>

        <div className={`${mobilePanelOpen ? "mt-3 block" : "hidden"} lg:mt-0 lg:block`}>
          <FilterPanel
            filters={filters}
            onChange={updateFilter}
            onReset={resetFilters}
            resultCount={result.length}
          />
        </div>

        {activeChips.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">已选：</span>
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => updateFilter(chip.key, "全部")}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-800"
              >
                {chip.value}
                <i className="ri-close-line text-xs w-3 h-3 flex items-center justify-center" />
              </button>
            ))}
            <button
              type="button"
              onClick={resetFilters}
              className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 underline-offset-4 hover:text-primary-700 hover:underline"
            >
              清除全部
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-foreground-500">
            第 {paged.currentPage} / {paged.totalPages} 页 · 共 {paged.totalItems} 部影片
          </p>
          <p className="hidden text-xs text-foreground-500 sm:block">
            当前排序：
            <span className="text-foreground-800">
              {filters.sort === "hot" ? "热门度" : filters.sort === "rating" ? "评分最高" : "最近更新"}
            </span>
          </p>
        </div>

        {paged.items.length > 0 ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
              {paged.items.map((movie, index) => (
                <MovieCard key={movie.id} movie={movie} index={index} />
              ))}
            </div>
            <Pagination
              currentPage={paged.currentPage}
              totalPages={paged.totalPages}
              onChange={changePage}
            />
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-search-line text-2xl w-6 h-6 flex items-center justify-center" />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-foreground-950">没有匹配的影片</h2>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
              当前筛选组合暂时没有结果，试着放宽年份或地区条件，或者直接重置筛选查看全部影片。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line text-base w-4 h-4 flex items-center justify-center" />
              重置筛选条件
            </button>
            <Link
              to="/"
              className="mt-3 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700"
            >
              返回首页看看热门推荐
            </Link>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}